// CatalogBrowser — the modal's `.ra-modal__catalog`: the "Browse the catalog"
// heading, the search + filter-dropdown row, and the card grid (or empty
// state). Pure presentation over a CatalogBrowse + the basket it toggles.
//
// Focused mode: when the modal was opened from a specific catalog item
// (`focusedCatalogItemId`), that item's name + resource cards are pinned at the
// top, then a divider, then the rest of the catalog (ALL resources across every
// catalog, minus the pinned item) under an "Add more resources to this request"
// heading — all shown by default, no expand step.

import { useMemo } from "react";
import { Box, Flex, Icon, VStack } from "@chakra-ui/react";
import { TbApps, TbBook2, TbSearchOff, TbX } from "react-icons/tb";
import PloyButton from "components/ds/PloyButton";
import SearchInput from "components/ds/SearchInput";
import CatalogCard from "./CatalogCard";
import FilterDropdown from "./FilterDropdown";
import { useContainerWidth } from "./useContainerWidth";
import type { CatalogBrowse } from "./useCatalogBrowse";
import type { Basket } from "./useBasket";
import { resourceKey, type FlatRequestItem } from "./types";

interface CatalogBrowserProps {
  catalog: CatalogBrowse;
  basket: Basket;
  /** When set, the modal was opened focused on a single catalog item. */
  focusedCatalogItemId?: string | null;
  /** resourceIds the member already holds active access to (badges the cards). */
  activeResourceIds?: Set<string>;
}

// Card-grid column count from the column's own width, not the viewport — the
// catalog column narrows when the basket opens (and again in the split tier).
// Thresholds keep every card at least ~140px wide before dropping a column.
const gridColsFor = (width: number) => (width >= 660 ? 3 : width >= 290 ? 2 : 1);

// Below this the full search + two split-pill dropdowns can't share a line —
// the dropdowns collapse to icon-only buttons and search takes the rest.
const COMPACT_FILTERS_BELOW = 760;

const Heading = ({ title, subtitle }: { title: string; subtitle?: string }) => (
  <>
    <Box fontFamily="body" fontWeight={600} fontSize="md" color="neutral.950" lineHeight="1">
      {title}
    </Box>
    {subtitle && (
      <Box fontSize="md" color="neutral.700" mt="2xs" lineHeight="1">
        {subtitle}
      </Box>
    )}
  </>
);

const CatalogBrowser = ({
  catalog,
  basket,
  focusedCatalogItemId = null,
  activeResourceIds,
}: CatalogBrowserProps) => {
  const hasFilters = Boolean(catalog.catalogFilter || catalog.appFilter);

  // 0 until first measure — fall back to the widest layout to avoid a
  // 1-column flash on open.
  const { ref: browseRef, width: browseW } = useContainerWidth<HTMLDivElement>();
  const measured = browseW > 0;
  const gridCols = measured ? gridColsFor(browseW) : 3;
  const compactFilters = measured && browseW < COMPACT_FILTERS_BELOW;
  const cardGrid = {
    display: "grid",
    // minmax(0, 1fr), NOT bare 1fr — a bare fr track's minimum is the
    // content's min-content, so one card anywhere in a column with a long
    // unbreakable name silently makes that whole column wider than its
    // siblings. Zeroing the minimum keeps every track hard-equal.
    gridTemplateColumns: `repeat(${gridCols}, minmax(0, 1fr))`,
    gap: "lg",
  } as const;

  // A card whose resource is in the basket via a DIFFERENT row (same resource
  // published in another catalog) renders "covered": still toggles, but shows
  // which catalog actually holds the selection instead of a second full
  // "Added" — so it's clear which card the request will be filed under.
  const directIds = new Set(basket.basketItems.map((b) => b.id));
  const holderByResource = new Map(
    basket.basketItems.map((b) => [resourceKey(b), b.catalogName]),
  );

  const renderCard = (item: FlatRequestItem) => (
    <CatalogCard
      key={item.id}
      item={item}
      added={basket.isAdded(item.id)}
      coveredBy={
        basket.isAdded(item.id) && !directIds.has(item.id)
          ? holderByResource.get(resourceKey(item))
          : undefined
      }
      onToggle={() => basket.toggle(item.id)}
      hasAccess={Boolean(
        activeResourceIds?.has(item.resource.selectableResource.resourceId),
      )}
    />
  );

  // The clicked catalog item's resource card(s) — pinned at the top in focused
  // mode. Derived straight from `flat` so it's independent of the filters.
  const focusedItems = useMemo(
    () =>
      focusedCatalogItemId
        ? catalog.flat.filter((it) => it.catalogItemId === focusedCatalogItemId)
        : [],
    [catalog.flat, focusedCatalogItemId],
  );
  const isFocused = focusedItems.length > 0;
  const focusedName = isFocused ? focusedItems[0].catalogItem.name : "";

  // The browse section (search + filters + grid). `gridItems` is parameterised
  // so the focused layout can exclude the already-pinned item.
  const renderBrowse = (gridItems: FlatRequestItem[]) => (
    <>
      {/* .ra-cat-filters — one line in compact: search fills, filters go
          icon-only. Wide keeps the wrapping split-pill row. */}
      <Flex
        align="center"
        gap="sm"
        flexWrap={compactFilters ? "nowrap" : "wrap"}
        mt="14px"
      >
        <Box flex="1 1 260px" minW={compactFilters ? "120px" : "220px"}>
          <SearchInput
            value={catalog.search}
            onChange={catalog.setSearch}
            placeholder="Search catalog items…"
          />
        </Box>
        <FilterDropdown
          icon={<Icon as={TbBook2} boxSize="16px" />}
          label="Catalog"
          allLabel="All catalogs"
          value={catalog.catalogFilter}
          options={catalog.catalogNames.map((c) => ({ id: c.id, label: c.name }))}
          onChange={catalog.selectCatalog}
          compact={compactFilters}
          menuAlign={compactFilters ? "right" : "left"}
        />
        <FilterDropdown
          icon={<Icon as={TbApps} boxSize="16px" />}
          label="Catalog item"
          allLabel="All items"
          value={catalog.appFilter}
          options={catalog.appsForCurrentCatalog.map((a) => ({ id: a.id, label: a.name }))}
          onChange={catalog.setAppFilter}
          compact={compactFilters}
          menuAlign={compactFilters ? "right" : "left"}
        />
        {hasFilters &&
          (compactFilters ? (
            <PloyButton
              variant="ghost"
              size="sm"
              onClick={() => catalog.selectCatalog(null)}
              aria-label="Clear filters"
              color="p.600"
              _hover={{ bg: "p.50", color: "p.700" }}
              px="1xs"
            >
              <Icon as={TbX} boxSize="14px" />
            </PloyButton>
          ) : (
            <PloyButton
              variant="ghost"
              size="sm"
              onClick={() => catalog.selectCatalog(null)}
              leftIcon={<Icon as={TbX} boxSize="14px" />}
              color="p.600"
              _hover={{ bg: "p.50", color: "p.700" }}
            >
              Clear filters
            </PloyButton>
          ))}
      </Flex>

      {/* .catalog-grid — 16px gap; columns from container width (3 → 2 → 1) */}
      {gridItems.length > 0 ? (
        <Box {...cardGrid} mt="lg">
          {gridItems.map(renderCard)}
        </Box>
      ) : (
        <VStack spacing="sm" color="neutral.600" fontSize="md" textAlign="center" mt="2xl" p="2xl">
          <Icon as={TbSearchOff} boxSize="28px" color="neutral.400" />
          <Box>No catalog items match your filters.</Box>
        </VStack>
      )}
    </>
  );

  // Focused: pinned item (name + cards) → divider → all other resources.
  if (isFocused) {
    // All resources (every catalog — filters start cleared in focused mode),
    // minus the pinned item so it isn't shown twice.
    const gridItems = catalog.filteredItems.filter(
      (it) => it.catalogItemId !== focusedCatalogItemId,
    );

    return (
      <Box ref={browseRef} flex="1" overflowY="auto" px="28px" pt="22px" pb="28px">
        <Heading title={focusedName} />
        <Box {...cardGrid} mt="14px">
          {focusedItems.map(renderCard)}
        </Box>

        <Box borderTopWidth="1px" borderTopStyle="solid" borderTopColor="neutral.100" my="lg" />

        <Heading
          title="Add more resources to this request"
          subtitle="Select any other resources you'd like to include"
        />
        {renderBrowse(gridItems)}
      </Box>
    );
  }

  return (
    <Box ref={browseRef} flex="1" overflowY="auto" px="28px" pt="22px" pb="28px">
      <Heading
        title="Browse the catalog"
        subtitle="Select the resources you'd like to gain access to"
      />
      {renderBrowse(catalog.filteredItems)}
    </Box>
  );
};

export default CatalogBrowser;
